import * as React from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch } from "../../store/hooks";
import { setTitleName } from "../../store/titleSlice";
import { Loader } from "../../components/layout/loader";
import { BillingAccount, BillingAccountAlert, BillingUserRole } from "../../schema/types/account";
import { request } from "../../services/cpln";
import { BillingUserInput } from "./create/input";
import { captureExc } from "../../errorBoundary";
import { v4 as uuidv4 } from "uuid";
import { IncorrectItem } from "../../components/generic/IncorrectItem/incorrectItem";
import { AccountDetailForm } from "./detailForm";

type RouteParams = "id";
export const BillingAccountDetailInitializer: React.FC = () => {
  const { id } = useParams<RouteParams>();
  const dispatch = useAppDispatch();

  const [account, setAccount] = React.useState<BillingAccount>(null as any);
  const [tou, setTou] = React.useState<boolean>(false);
  const [roles, setRoles] = React.useState<BillingUserRole[]>([]);
  const [users, setUsers] = React.useState<BillingUserInput[]>([]);
  const [alert, setAlert] = React.useState<BillingAccountAlert>(null as any);
  const [orgNames, setOrgNames] = React.useState<string[]>([]);
  const hasOrg = orgNames.length > 0;
  const [isLoading, setIsLoading] = React.useState(true);
  const [isIncorrect, setIsIncorrect] = React.useState(false);

  React.useEffect(() => {
    fetchItem();
  }, [id]);

  // Set document title name from account data
  React.useEffect(() => {
    const titleName = account?.accountName || account?.fullName || id;
    dispatch(setTitleName(titleName || null));
    return () => {
      dispatch(setTitleName(null));
    };
  }, [account?.accountName, account?.fullName, id]);

  async function fetchItem() {
    try {
      setIsLoading(true);
      const { data } = await request<BillingAccount>({ service: "billing-ng", url: `/account/${id}` });
      setAccount(data);
      await Promise.allSettled([fetchTou(), fetchRoles(), fetchUsers(), fetchAlert(), fetchOrgs()]);
      setIsLoading(false);
    } catch (e) {
      captureExc(e);
      setIsIncorrect(true);
      setIsLoading(false);
    }
  }

  async function fetchTou() {
    try {
      await request({ service: "billing-ng", url: `/account/${id}/tou` });
      setTou(true);
    } catch (e) {
      setTou(false);
    }
  }

  async function fetchRoles() {
    try {
      const { data } = await request({ service: "billing-ng", url: `/account/${id}/user/self` });
      if (data.admin === true) {
        setRoles(["billing_admin"]);
      } else {
        setRoles(data.roles || []);
      }
    } catch (e) {
      setRoles([]);
    }
  }

  async function fetchUsers() {
    try {
      const { data } = await request({ service: "billing-ng", url: `/account/${id}/user` });
      setUsers((data.items || []).map((u) => ({ id: uuidv4(), ...u })));
    } catch (e) {}
  }

  async function fetchAlert() {
    try {
      const { data } = await request({ service: "billing-ng", url: `/account/${id}/alert` });
      setAlert(data);
    } catch (e) {}
  }

  async function fetchOrgs() {
    try {
      const { data } = await request({ service: "billing-ng", url: `/account/${id}/org` });
      setOrgNames(data.items.map((i) => i.name));
    } catch (e) {
      setOrgNames([]);
    }
  }

  async function fetchPaymentMethods() {
    const previousAccount = JSON.parse(JSON.stringify(account));
    try {
      const { data } = await request<BillingAccount>({ service: "billing-ng", url: `/account/${id}` });
      setAccount({ ...account, paymentMethods: data.paymentMethods });
    } catch (e) {
      setAccount(previousAccount);
    }
  }

  if (isLoading) {
    return <Loader fullScreen />;
  }

  if (isIncorrect) {
    return <IncorrectItem kind={"account"} canCreate={false} />;
  }

  return (
    <AccountDetailForm
      account={account}
      tou={tou}
      users={users}
      roles={roles}
      orgNames={orgNames}
      hasOrg={hasOrg}
      alert={alert}
      fetchPaymentMethods={fetchPaymentMethods}
      fetchAccount={fetchItem}
    />
  );
};
